import { useMemo } from 'react';

import {
  Building2,
  ChartSpline,
  LogOut,
  Sparkles,
  UserRoundPen,
  UsersRound
} from 'lucide-react';
import { useHistory } from 'react-router';

import { Button } from '@/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/shadcn/dropdown-menu';

import { logout } from '@/client/account';
import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { parseEntities } from '@/components/userDashboard/utils';
import { env } from '@/constant/env';
import { ROUTE_LOGIN } from '@/constant/routes';
import { useUser } from '@/hooks/useUser';

export const NavUser = () => {
  const { user, isPrivileged, loading } = useUser();
  const history = useHistory();

  const { organization } = useMemo(
    () => parseEntities(user?.memberOf.nodes || []),
    [user?.memberOf.nodes]
  );

  const onSignOut = async () => {
    await logout();
    history.push(ROUTE_LOGIN);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={loading}>
        <Button
          className="size-9 rounded-full p-0"
          disabled={loading}
          size="icon"
          variant="ghost"
        >
          <AvatarComponent name={user?.name} size={36} source={user?.avatar} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        side="bottom"
        sideOffset={4}
      >
        {user && (
          <>
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <AvatarComponent
                  name={user.name}
                  size={32}
                  source={user.avatar}
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-muted-foreground text-xs">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <a
              className="flex w-full items-center gap-2 text-foreground"
              draggable="false"
              href={env.VITE_ACCOUNT_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              <UserRoundPen /> My profile
            </a>
          </DropdownMenuItem>
          {organization?.rowId && (
            <>
              <DropdownMenuItem>
                <a
                  className="flex w-full items-center gap-2 text-foreground"
                  draggable="false"
                  href={`${env.VITE_ACCOUNT_URL}/organization/${organization?.rowId}/members`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Building2 /> Organization
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  className="flex w-full items-center gap-2 text-foreground"
                  draggable="false"
                  href={`${env.VITE_ACCOUNT_URL}/organization/${organization?.rowId}/teams`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <UsersRound /> Teams
                </a>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <a
              className="flex w-full items-center gap-2 text-foreground"
              draggable="false"
              href={env.VITE_ANALYTICS_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ChartSpline /> Analytics
            </a>
          </DropdownMenuItem>

          {isPrivileged && (
            <DropdownMenuItem>
              <a
                className="flex w-full items-center gap-2 text-foreground"
                draggable="false"
                href={env.VITE_STUDIO_URL}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Sparkles /> Studio
              </a>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onSignOut()} variant="destructive">
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
